// useRecomputeStatus — polls GET /api/admin/recompute-rankings/status while
// a recompute is in-flight (or while the caller's `enabled` flag is true).
// Mirrors useJobStatus terminal-state pattern: refetchInterval returns
// 3000 when polling is desired, else false. staleTime 30s lets the page
// read the last known state on mount without burning a fetch.
//
// The caller passes `enabled` to keep polling for a few seconds AFTER the
// server flips `running` to false — gives the UI time to render the "Done"
// banner before polling stops. AdminPage manages this via local state.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchRecomputeStatus } from '@/lib/api';
import type { RecomputeStatusDTO } from '@/types/db';

const POLL_INTERVAL_MS = 3000;

export function useRecomputeStatus(
  enabled: boolean = false,
): UseQueryResult<RecomputeStatusDTO, Error> {
  return useQuery<RecomputeStatusDTO, Error>({
    queryKey: ['recompute-status'],
    queryFn: ({ signal }) => fetchRecomputeStatus(signal),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data as RecomputeStatusDTO | undefined;
      if (data?.running === true) return POLL_INTERVAL_MS;
      if (enabled) return POLL_INTERVAL_MS;
      return false;
    },
    refetchIntervalInBackground: false,
    retry: 1,
  });
}
